import {ButtonProps, Card, Separator, showNotification} from 'platform/components';
import {Box, VStack} from 'platform/foundation';

import {useCallback, useState} from 'react';

import {head, isNil, mergeAll} from 'ramda';

import {
  BulkPostSupplierOrderItemsRequest,
  useBulkPostSupplierOrderItemsMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {DmsDataGrid, FullScreenModal, WAREHOUSE_GRID_CODES, handleApiError} from '@dms/shared';

import {composePath, Nullish, suffixTestId, TestIdProps, useNavigate} from 'shared/web';

import type {QueryFilterObject, RowData} from 'features/eag-grid';

import {AddToSupplierOrderExternalFilter} from './AddToSupplierOrderExternalFilter';

const SUPPLIER_ORDER_PENDING_LIST_EXTERNAL_FILTER_ID =
  'supplier-order-pending-list-external-filter';

interface AddToSupplierOrderModalProps extends TestIdProps {
  itemId: string[];
  warehouseId: string;
  onClose: () => void;
}

export function AddToSupplierOrderModal(props: AddToSupplierOrderModalProps) {
  const navigate = useNavigate();

  const [selectedSupplierOrder, setSelectedSupplierOrder] = useState<string | Nullish>(null);

  const [postSupplierOrderItems, {isLoading: isAddingSupplierOrderItems}] =
    useBulkPostSupplierOrderItemsMutation();

  const handleAddItems = async () => {
    if (isNil(selectedSupplierOrder)) {
      return;
    }

    const request: BulkPostSupplierOrderItemsRequest = {
      orderId: selectedSupplierOrder,
      body: {requestItemId: props.itemId, itemState: 'ORDER'},
    };

    await postSupplierOrderItems(request)
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('page.warehouse.notification.supplierOrderCreated'));

        navigate(
          composePath(warehouseRoutes.supplierOrderDetailOverview, {
            params: {
              id: selectedSupplierOrder,
            },
          })
        );
      })
      .catch(handleApiError);
  };

  const handleOnRowSelectionChange = (rows: RowData[]) => {
    setSelectedSupplierOrder(head(rows)?.id);
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {warehouseId: props.warehouseId}]),
    [props.warehouseId]
  );

  const actions: ButtonProps[] = [
    {
      title: i18n.t('general.actions.discard'),
      variant: 'secondary',
      onClick: props.onClose,
      'data-testid': suffixTestId('actions.discard', props),
    },
    {
      isLoading: isAddingSupplierOrderItems,
      type: 'submit',
      title: i18n.t('general.actions.add'),
      onClick: handleAddItems,
      'data-testid': suffixTestId('actions.add', props),
    },
  ];

  return (
    <FullScreenModal headline={i18n.t('general.labels.addToSupplierOrder')} actions={actions}>
      <Box padding={4} height="100%">
        <VStack spacing={4} height="100%">
          <Card isFullHeight>
            {/* Element for DG external filter */}
            {}
            <div css={{width: '100%'}} id={SUPPLIER_ORDER_PENDING_LIST_EXTERNAL_FILTER_ID} />
            <Separator />
            <Box height="90%">
              <DmsDataGrid
                gridCode={WAREHOUSE_GRID_CODES.warehouseSupplierOrdersPending}
                onRowSelectionChange={handleOnRowSelectionChange}
                externalFilterId={SUPPLIER_ORDER_PENDING_LIST_EXTERNAL_FILTER_ID}
                queryModifier={queryModifier}
                filterComponent={(datagridProps) => (
                  <AddToSupplierOrderExternalFilter
                    warehouseId={props.warehouseId}
                    {...datagridProps}
                  />
                )}
                data-testid={suffixTestId('supplierOrderPendingList', props)}
              />
            </Box>
          </Card>
        </VStack>
      </Box>
    </FullScreenModal>
  );
}
